<script setup lang="ts">
/**
 * The first frame: the game's own sea, live, with a battleship under way
 * (`LandingHero.vue`, lazy: the words and the plate never wait for the 3D
 * chunk; the dusk painting stands in until it arrives), the title in outlined
 * game lettering, one line, three figures in kit chips and the PLAY FREE
 * plate beside a quiet SIGN IN plate.
 *
 * `data-test="landing-play"` / `"landing-signin"` are what the e2e scripts
 * click: keep them on these two buttons.
 */
import { defineAsyncComponent, defineComponent, h, onBeforeUnmount, onMounted, ref } from 'vue';

import { useI18n } from '@/i18n';
import GameIcon from '@/ui/kit/GameIcon.vue';
import type { GameIconName } from '@/ui/kit/icons';
import ActionButton from '@/ui/panel/ActionButton.vue';

import { playHeroEntrance } from '../hero-entrance';
import { FIGURES } from '../landing-content';
import RollingNumber from '../RollingNumber.vue';

defineEmits<{ play: []; signIn: [] }>();
const { t } = useI18n();

/** The hero's painting alone, for the moment before its chunk arrives. */
const HeroPainting = defineComponent({
  setup: () => () =>
    h('div', { class: 'wait', 'aria-hidden': 'true' }, [
      h('picture', {}, [
        h('source', { srcset: '/art/loading-dusk.webp', type: 'image/webp' }),
        h('img', { src: '/art/loading-dusk.jpg', alt: '', decoding: 'async', fetchpriority: 'high' }),
      ]),
    ]),
});

// Lazy: the hero is what pulls Three.js and the engine.
const LandingHero = defineAsyncComponent({
  loader: () => import('../LandingHero.vue'),
  loadingComponent: HeroPainting,
});

const CHIPS: readonly { icon: GameIconName; value: number; labelKey: string }[] = [
  { icon: 'warship', value: FIGURES.hulls, labelKey: 'landing.hero.chip.hulls' },
  { icon: 'flag', value: FIGURES.modes, labelKey: 'landing.hero.chip.modes' },
  { icon: 'medal', value: FIGURES.ranks, labelKey: 'landing.hero.chip.ranks' },
  { icon: 'hourglass', value: FIGURES.battleMinutes, labelKey: 'landing.hero.chip.minutes' },
];

const copyEl = ref<HTMLElement | null>(null);
let stopEntrance: () => void = () => undefined;
onMounted(() => {
  if (copyEl.value) stopEntrance = playHeroEntrance(copyEl.value);
});
onBeforeUnmount(() => stopEntrance());
</script>

<template>
  <section class="hero" data-section="top" data-parallax>
    <LandingHero :active="true" />
    <div class="shade" aria-hidden="true"></div>
    <div ref="copyEl" class="copy">
      <h1 class="k-title title" data-hero="title">
        <span class="line">{{ t('landing.hero.title1') }}</span>
        <span class="line gold">{{ t('landing.hero.title2') }}</span>
      </h1>
      <p class="tagline" data-hero="copy">{{ t('landing.hero.tagline') }}</p>
      <ul class="chips" data-hero="chips">
        <li v-for="c in CHIPS" :key="c.labelKey" class="k-chip chip">
          <GameIcon :name="c.icon" :size="34" />
          <span class="figure"><RollingNumber :value="c.value" /></span>
          <span class="k-label chip-label">{{ t(c.labelKey) }}</span>
        </li>
      </ul>
      <div class="cta" data-hero="cta">
        <ActionButton variant="primary" size="lg" cta class="play" data-test="landing-play" @click="$emit('play')">
          <GameIcon name="warship" :size="48" />
          {{ t('landing.hero.play') }}
        </ActionButton>
        <ActionButton variant="quiet" size="md" data-test="landing-signin" @click="$emit('signIn')">
          {{ t('landing.hero.signIn') }}
        </ActionButton>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hero {
  position: relative;
  display: grid;
  align-items: end;
  min-height: 100dvh;
  overflow: hidden;
  background: #06090d;
}

.wait,
.wait :deep(img) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Keeps the lettering readable over the bright sea, and melts into the next section. */
.shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(0deg, rgba(8, 14, 21, 1) 0%, rgba(8, 14, 21, 0.2) 22%, rgba(8, 14, 21, 0) 45%),
    linear-gradient(90deg, rgba(8, 14, 21, 0.62), rgba(8, 14, 21, 0) 65%);
  pointer-events: none;
}

.copy {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: clamp(12px, 2.4vh, 22px);
  justify-items: start;
  min-width: 0;
  padding: calc(84px + env(safe-area-inset-top, 0px)) clamp(16px, 5vw, 72px) clamp(54px, 9vh, 96px);
}

.title {
  display: grid;
  font-size: clamp(2.6rem, 0.6rem + 10.5vw, 6.4rem);
  text-transform: uppercase;
  line-height: 0.92;
  filter: drop-shadow(0 8px 0 rgba(13, 20, 28, 0.5));
}

.line.gold {
  color: var(--kit-gold);
}

.tagline {
  margin: 0;
  font-family: var(--font-game-body);
  font-weight: 700;
  font-size: clamp(1.05rem, 0.9rem + 0.8vw, 1.5rem);
  color: #fff;
  text-shadow: 0 2px 0 var(--kit-ink), 0 0 14px rgba(13, 20, 28, 0.9);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.chip {
  min-height: 44px;
  padding-right: 0.9rem;
  font-size: 1.25rem;
}

.figure {
  -webkit-text-stroke: var(--kit-stroke) var(--kit-ink);
  paint-order: stroke fill;
}

.chip-label {
  font-size: 0.8rem;
  text-transform: uppercase;
  opacity: 0.9;
}

.cta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px 18px;
  margin-top: 4px;
}

.play {
  --h: 4.6rem;
  padding-inline: 2.4rem 2.8rem;
}

@media (min-width: 640px) {
  .line {
    white-space: nowrap;
  }
}

@media (max-width: 640px) {
  .copy {
    justify-items: center;
    text-align: center;
  }

  .chips,
  .cta {
    justify-content: center;
  }
}

/* A phone on its side: title left, plates right, nothing taller than the screen. */
@media (max-height: 520px) {
  .copy {
    padding-top: 64px;
    padding-bottom: 36px;
    gap: 8px;
  }

  .title {
    font-size: clamp(2.2rem, 11vh, 4rem);
  }

  .chips {
    display: none;
  }

  .play {
    --h: 3.4rem;
  }
}
</style>
